<script context="module">
  const { BLOCKS, INLINES } = types;
  import Heading from "./Heading.svelte";

  // ESCAPE HATCH.
  // REMOVE THIS WHEN HEADINGS ARE NO LONGER LINKS IN CONTENTFUL
  // AND VALIDATIONS ARE IN PLACE
  // --- FROM HERE --
  function hyperlinkToText(contentArray) {
    const transformed = contentArray.map((a) => {
      if (a.nodeType == "hyperlink") {
        // return the text content of the hyperlink
        return a.content[0];
      }
      return a;
    });
    // combine the values to make it one string
    const string = transformed.reduce((acc, c) => {
      return (acc += c.value);
    }, "");

    return string;
  }

  function getHeadingText(n) {
    if (n.content.length > 1) {
      return hyperlinkToText(n.content);
    } else if (n.content[0]) {
      return n.content[0].value;
    }
  }

  export function createHeading(el, props) {
    const text = getHeadingText(el);
    // explicitly skip headings with no content
    if (!text || text.match(/^\s*$/)) {
      // return an iterable value, but undefined.
      return [undefined, undefined];
    }
    return [Heading, { ...props, text }];
  }
  // --- TO HERE

  function hasText(el) {
    let foundText = false;
    el.content.forEach((content) => {
      // explicitly skip paragraphs with no content, but sometimes they may also just be a hyperlink
      //or maybe something else too?
      if (content.nodeType !== "text" || (content.value && !content.value.match(/^\s*$/))) {
        foundText = true;
      }
    });

    return foundText;
  }

  export function createParagraph(el, props) {
    if (!hasText(el)) {
      // return an iterable value, but undefined.
      return [undefined, undefined];
    }

    if (el.content.length > 1) {
      props.class += " multi-content";
    }

    return ["p", props];
  }

  export function createListItem(el, props) {
    // explicitly skip list items with no content
    if (!hasText(el)) {
      // return an iterable value, but undefined.
      return [undefined, undefined];
    }

    return ["li", props];
  }

  export function getClassesFromMarks(marks) {
    let textCss = "";
    marks.forEach((mark) => {
      switch (mark.type) {
        case "bold":
          textCss += " font-bold bold-text";
          return;
        case "italic":
          textCss += " italic";
          return;
        case "underline":
          textCss += " underline";
          return;
      }
    });
    return textCss;
  }

  export function isSvelteComponent(nodeType) {
    return [
      BLOCKS.EMBEDDED_ENTRY,
      INLINES.EMBEDDED_ENTRY,
      INLINES.HYPERLINK,
      INLINES.ASSET_HYPERLINK,
      BLOCKS.HEADING_1,
      BLOCKS.HEADING_2,
      BLOCKS.HEADING_3,
      BLOCKS.HEADING_4,
      BLOCKS.HEADING_5,
      BLOCKS.HEADING_6
    ].includes(nodeType);
  }
</script>

<script>
  import * as types from "@contentful/rich-text-types";
  import Element from "$lib/components/Element.svelte";
  import Link from "$lib/components/Link.svelte";
  import { error } from "@sveltejs/kit";
  import { BASE_COMPONENT_MAP } from "$lib/components/ComponentMapBuilder.svelte";

  const { helpers } = types;

  let clazz = "";
  export { clazz as class };
  export let node;
  export let textId;
  export let componentMap = BASE_COMPONENT_MAP;

  if (!node) {
    throw error(500, "<RichText /> requires a `node` parameter.");
  }

  function createComponent(typename, data, validComponents) {
    if (typename === "freeRichTextComponent") {
      return ["self", data];
    }
    let component = validComponents[typename];
    if (!component) {
      // eslint-disable-next-line no-console
      console.warn(`No component: ${typename}`);
    }
    let d = component ? { data } : undefined;
    return [component, d];
  }

  function createRenderable(n) {
    const data = n.data.target;
    const typename = n.data.target?.sys?.contentType?.sys.id;

    const MAP = {
      [BLOCKS.DOCUMENT]: () => ["div", { class: "embedded-content" }],
      [BLOCKS.PARAGRAPH]: (el) => createParagraph(el, { class: "rich-text-paragraph" }),
      [BLOCKS.HEADING_1]: (el) => createHeading(el, { class: "heading long-heading-1" }),
      [BLOCKS.HEADING_2]: (el) => createHeading(el, { class: "heading heading-2" }),
      [BLOCKS.HEADING_3]: (el) => createHeading(el, { class: "heading heading-3" }),
      [BLOCKS.HEADING_4]: (el) => createHeading(el, { class: "heading heading-4" }),
      [BLOCKS.HEADING_5]: (el) => createHeading(el, { class: "heading heading-5" }),
      [BLOCKS.HEADING_6]: (el) => createHeading(el, { class: "heading " }), //This is deprecated, no styling.
      [BLOCKS.EMBEDDED_ENTRY]: () => createComponent(typename, data, componentMap),
      [BLOCKS.UL_LIST]: () => ["ul", { class: "embedded-list" }],
      [BLOCKS.OL_LIST]: () => ["ol", { class: "embedded-list" }],
      [BLOCKS.LIST_ITEM]: (el) => createListItem(el, { class: "" }),
      [BLOCKS.QUOTE]: () => ["blockquote", {}],
      [BLOCKS.HR]: () => ["hr", { "aria-hidden": true }],
      [INLINES.ASSET_HYPERLINK]: (el) => [
        Link,
        {
          text: el.content[0]?.value,
          href: el.data?.target?.fields?.file?.url,
          url: el.data?.target?.fields?.file?.url,
          class: "!p-0"
        }
      ],
      [INLINES.ENTRY_HYPERLINK]: () => ["img", {}],
      [INLINES.EMBEDDED_ENTRY]: () => createComponent(typename, data, componentMap),
      [INLINES.HYPERLINK]: (el) => [
        Link,
        {
          text: el.content[0]?.value,
          href: el.data.uri,
          url: el.data.uri,
          class: "!p-0"
        }
      ]
    };

    return MAP[n.nodeType](n);
  }

  const [elementOrComponent, props] = createRenderable(node);
  const IS_NOT_SKIPPED = elementOrComponent !== undefined;

  const IS_SVELTE_COMPONENT = isSvelteComponent(node.nodeType);
</script>

{#if IS_SVELTE_COMPONENT && IS_NOT_SKIPPED}
  {#if elementOrComponent === "self"}
    <!-- Element is a FreeRichTextComponent -->
    <svelte:self node={props.fields?.richTextContent} {textId} class={clazz} {componentMap} />
  {:else}
    <svelte:component this={elementOrComponent} {...props} class="{props.class} {clazz}" />
  {/if}
{:else if IS_NOT_SKIPPED}
  <Element {textId} type={elementOrComponent} {...props} class="{props.class} {clazz}">
    {#each node.content as component}
      {#if helpers.isText(component)}
        {#if component.marks && component.marks.length > 0}
          <span class={getClassesFromMarks(component.marks)}>{component.value}</span>
        {:else}
          {component.value}
        {/if}
      {:else}
        <svelte:self node={component} {textId} class={clazz} {componentMap} />
      {/if}
    {/each}
  </Element>
{/if}
